import { Text, Heading } from '@everlywell/ui-kit';
import React from 'react';

export type SectionHeaderProps = {
  eyebrow?: string | null;
  title?: string | null;
  subtitle?: string | null;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  headerAlignment?: Parameters<typeof Heading>[0]['textAlign'];
  eyebrowAlignment?: Parameters<typeof Text>[0]['textAlign'];
  subtitleAlignment?: Parameters<typeof Text>[0]['textAlign'];
  headingSize?: Parameters<typeof Heading>[0]['size'];
  subtitleSize?: Parameters<typeof Text>[0]['size'];
  eyebrowColor?: Parameters<typeof Text>[0]['color'];
  titleColor?: Parameters<typeof Heading>[0]['color'];
};

/**
 * A component that renders a section header. It can display an eyebrow and/or title.
 * @param {SectionHeaderProps} props - The props for the component.
 * @param {string} [props.eyebrow] - The eyebrow text.
 * @param {string} [props.title] - The title text.
 * @param {string} [props.headingLevel] - The heading level for the title. Defaults to 'h2'.
 * @param {string} [props.headingSize] - The size for the heading. Defaults to '2xl'.
 * @param {string} [props.headerAlignment] - The alignment for the title. Defaults to 'center'.
 * @param {string} [props.eyebrowAlignment] - The alignment for the eyebrow. Defaults to 'center'.
 * @param {string} [props.subtitle] - The subtitle text.
 * @param {string} [props.subtitleAlignment] - The alignment for the subtitle. Defaults to 'center'.
 * @param {string} [props.subtitleSize] - The size for the subtitle. Defaults to 'xl'.
 */
export default function SectionHeader({
  eyebrow,
  title,
  subtitle,
  headingLevel = 'h2',
  headingSize = '2xl',
  headerAlignment = 'center',
  eyebrowAlignment = 'center',
  subtitleAlignment = 'center',
  subtitleSize = 'xl',
  eyebrowColor = 'viridian.base',
  titleColor = 'tints.black',
}: SectionHeaderProps) {
  return (
    <>
      {eyebrow ? (
        <Text
          marginX="auto"
          size="lg"
          color={eyebrowColor}
          textAlign={eyebrowAlignment}
          marginBottom={[1, null, 2]}
        >
          {eyebrow}
        </Text>
      ) : null}

      {title ? (
        <Heading
          as={headingLevel}
          size={headingSize}
          textAlign={headerAlignment}
          marginBottom={subtitle ? [3, 4] : [6, null, 10]}
          color={titleColor}
        >
          {title}
        </Heading>
      ) : null}
      {subtitle ? (
        <Text
          textAlign={subtitleAlignment}
          marginBottom={[6, null, 10]}
          maxWidth={['100%', null, '650px']}
          marginRight="auto"
          marginLeft="auto"
          size={subtitleSize}
        >
          {subtitle}
        </Text>
      ) : null}
    </>
  );
}
